import React, { useEffect, useMemo, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  addLayoutSaveMark,
  addLayoutSaveMarks,
  addTemplate,
  beginEditInstance,
  clearLayoutSaveMarks,
  duplicateInstance,
  makeTemplateFromInstance,
  removeInstance,
  setPortEditInstance,
  toggleLayoutSaveMark,
} from '../../features/equipment/equipmentSlice';
import { openRightDrawerTab } from '../../features/ui/uiSlice';
import {
  IconDownload,
  IconDuplicate,
  IconLayers,
  IconSliders,
  IconTrash,
} from '../icons/ContextMenuIcons';
import { OBJECT_TYPE_LABELS, CATEGORY_LABELS } from '../../features/equipment/equipmentConstants';
import { isDxfInsertStocker } from '../../utils/stockerExport';
import { equipmentProvenance } from '../../utils/equipmentGeometry';
import type { EquipmentInstance } from '../../features/equipment/equipmentTypes';

export type EquipmentContextMenuState = {
  x: number;
  y: number;
  instanceId: string;
};

interface Props {
  menu: EquipmentContextMenuState;
  containerWidth: number;
  containerHeight: number;
  onClose: () => void;
}

const MENU_W = 300;
const MENU_H = 520;

const isStockerInstance = (inst: EquipmentInstance): boolean =>
  isDxfInsertStocker(inst) || inst.objectType === 'stocker';

const EquipmentContextMenu: React.FC<Props> = ({
  menu,
  containerWidth,
  containerHeight,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const menuRef = useRef<HTMLDivElement>(null);
  const instance = useAppSelector((s) => s.equipment.instances[menu.instanceId]);
  const instances = useAppSelector((s) => s.equipment.instances);
  const instanceOrder = useAppSelector((s) => s.equipment.instanceOrder);
  const templates = useAppSelector((s) => s.equipment.templates);
  const portEditId = useAppSelector((s) => s.equipment.portEditInstanceId);
  const markedIds = useAppSelector((s) => s.equipment.layoutSaveMarkedIds);

  const { stockerIds, equipmentIds } = useMemo(() => {
    const stockers: string[] = [];
    const equipment: string[] = [];
    for (const id of instanceOrder) {
      const inst = instances[id];
      if (!inst) continue;
      if (isStockerInstance(inst)) stockers.push(id);
      else equipment.push(id);
    }
    return { stockerIds: stockers, equipmentIds: equipment };
  }, [instanceOrder, instances]);

  useEffect(() => {
    const handlePointerDown = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  if (!instance) return null;

  const left =
    menu.x + MENU_W + 8 > containerWidth ? Math.max(8, menu.x - MENU_W) : Math.max(8, menu.x);
  const top = Math.min(Math.max(8, menu.y), Math.max(8, containerHeight - MENU_H));

  const itemClass =
    'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-medium text-gray-800 hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-40';
  const dividerClass = 'my-1 border-t border-gray-200';

  const isStocker = isStockerInstance(instance);
  const isMarked = markedIds.includes(instance.id);
  const isLayoutSaved = equipmentProvenance(instance) === 'layout';
  const markedCount = markedIds.length;
  const itemLabel = isStocker ? 'stocker' : 'equipment';

  const handleEdit = () => {
    dispatch(beginEditInstance(instance.id));
    onClose();
  };

  const handleEditPorts = () => {
    dispatch(setPortEditInstance(portEditId === instance.id ? null : instance.id));
    onClose();
  };

  const handleDuplicate = () => {
    dispatch(duplicateInstance(instance.id));
    onClose();
  };

  const handleSaveAsTemplate = () => {
    const name = window.prompt('Template name', instance.name || 'New Template');
    if (!name) {
      onClose();
      return;
    }
    dispatch(addTemplate(makeTemplateFromInstance(instance, templates, name)));
    dispatch(openRightDrawerTab('equipment'));
    onClose();
  };

  /** Add to the save list — File → Save embeds these in the layout JSON elements. */
  const handleAddToSaveList = () => {
    dispatch(addLayoutSaveMark(instance.id));
    onClose();
  };

  const handleToggleMark = () => {
    dispatch(toggleLayoutSaveMark(instance.id));
    onClose();
  };

  const handleSaveAllStockers = () => {
    dispatch(addLayoutSaveMarks(stockerIds));
    onClose();
  };

  const handleSaveAllEquipment = () => {
    dispatch(addLayoutSaveMarks(equipmentIds));
    onClose();
  };

  const handleClearMarks = () => {
    dispatch(clearLayoutSaveMarks());
    onClose();
  };

  const handleDelete = () => {
    dispatch(removeInstance(instance.id));
    onClose();
  };

  return (
    <div
      ref={menuRef}
      className="absolute z-30 rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl"
      style={{ left, top, width: MENU_W }}
      role="menu"
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="flex items-center gap-3 px-3 py-2.5">
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-base"
          aria-hidden="true"
        >
          {instance.overrides.style?.icon || '🛠'}
        </div>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-gray-900">
            {instance.name || 'Equipment'}
          </div>
          <div className="truncate text-xs text-gray-500">
            {OBJECT_TYPE_LABELS[instance.objectType]} · {CATEGORY_LABELS[instance.category]}
          </div>
        </div>
      </div>

      <div className={dividerClass} />

      <button type="button" className={itemClass} role="menuitem" onClick={handleEdit}>
        <IconSliders />
        <span className="flex-1">Edit properties…</span>
      </button>
      {isLayoutSaved && (
        <button type="button" className={itemClass} role="menuitem" onClick={handleEditPorts}>
          <IconLayers />
          <span className="flex-1">
            {portEditId === instance.id ? 'Finish editing ports' : 'Edit ports'}
          </span>
        </button>
      )}
      <button type="button" className={itemClass} role="menuitem" onClick={handleDuplicate}>
        <IconDuplicate />
        <span className="flex-1">Duplicate</span>
      </button>
      <button type="button" className={itemClass} role="menuitem" onClick={handleSaveAsTemplate}>
        <IconLayers />
        <span className="flex-1">Save as template…</span>
      </button>

      <div className={dividerClass} />
      {!isMarked ? (
        <button type="button" className={itemClass} role="menuitem" onClick={handleAddToSaveList}>
          <IconDownload />
          <span className="flex-1">Add {itemLabel} to save list</span>
        </button>
      ) : (
        <button type="button" className={itemClass} role="menuitem" onClick={handleToggleMark}>
          <IconLayers />
          <span className="flex-1">Remove from save list</span>
        </button>
      )}
      <button
        type="button"
        className={itemClass}
        role="menuitem"
        disabled={stockerIds.length === 0}
        onClick={handleSaveAllStockers}
      >
        <IconDownload />
        <span className="flex-1">Save all stockers ({stockerIds.length})</span>
      </button>
      <button
        type="button"
        className={itemClass}
        role="menuitem"
        disabled={equipmentIds.length === 0}
        onClick={handleSaveAllEquipment}
      >
        <IconDownload />
        <span className="flex-1">Save all equipment ({equipmentIds.length})</span>
      </button>

      {markedCount > 0 && (
        <>
          <div className="px-3 py-1.5 text-xs text-gray-500">
            {markedCount} item{markedCount === 1 ? '' : 's'} in save list — written into elements on
            File → Save
          </div>
          <button type="button" className={itemClass} role="menuitem" onClick={handleClearMarks}>
            <IconLayers />
            <span className="flex-1">Clear save list</span>
          </button>
        </>
      )}

      <div className={dividerClass} />

      <button
        type="button"
        className={`${itemClass} text-red-600 hover:bg-red-50`}
        role="menuitem"
        onClick={handleDelete}
      >
        <IconTrash />
        <span className="flex-1">Delete</span>
      </button>
    </div>
  );
};

export default EquipmentContextMenu;
